import { z } from 'zod'

// --- Enums (from litter-deal-flow mockData & AccountDetail UI) ---
export const FinancialAccountTypeSchema = z.enum(['Credit', 'proForma'])
export const CreditStatusSchema = z.enum(['approved', 'blocked'])
export const AccountStatusSchema = z.enum([
    'Approved',
    'Cancelled',
    'Business Pro',
    'Blocked',
    'Ad Hoc Approved',
    'Rejected',
    'Manually Paused',
])
export const TargetAccountStatusSchema = z.enum([
    'Lite',
    'Key',
    'Enterprise',
    'Diamond',
])
export const SpendLevelSchema = z.enum(['Lite', 'Enterprise', 'Key', 'Diamond'])
export const SpendPerformanceSchema = z.enum([
    'Stable',
    'Growing',
    'Reducing',
    'Dormant',
])
export const TargetSpendDeltaSchema = z.enum([
    'Significantly Under Target',
    'Moderately Under Target',
    'At Lower Band of Target',
    'Within Target Range',
    'Upper Band of Target',
    'Significantly Above Target',
    'Potential Upgrade',
])
export const AccountLevelSchema = z.enum([
    'Lite',
    'Enterprise',
    'Key',
    'Diamond',
])
export const AccountClassificationSchema = z.enum([
    'R1',
    'R2 Managed',
    'R2 Unmanaged',
])
export const WasteReportingLevelSchema = z.enum([
    'NONE',
    'LITE',
    'STANDARD',
    'FULL',
    'PRO',
])

export type FinancialAccountType = z.infer<typeof FinancialAccountTypeSchema>
export type CreditStatus = z.infer<typeof CreditStatusSchema>
export type AccountStatus = z.infer<typeof AccountStatusSchema>
export type TargetAccountStatus = z.infer<typeof TargetAccountStatusSchema>
export type SpendLevel = z.infer<typeof SpendLevelSchema>
export type SpendPerformance = z.infer<typeof SpendPerformanceSchema>
export type TargetSpendDelta = z.infer<typeof TargetSpendDeltaSchema>
export type AccountLevel = z.infer<typeof AccountLevelSchema>
export type AccountClassification = z.infer<typeof AccountClassificationSchema>
export type WasteReportingLevel = z.infer<typeof WasteReportingLevelSchema>

// --- Monthly Spend (used in contacts and account) ---
export const CanonicalMonthlySpendSchema = z
    .object({
        bookings: z.number(),
        month: z.string(),
        spend: z.number(),
    })
    .strict()

export type CanonicalMonthlySpend = z.infer<typeof CanonicalMonthlySpendSchema>

// --- Commercial Contact ---
export const CanonicalCommercialContactSchema = z
    .object({
        accountActivityStatus: z.string().nullable(),
        accountSpendLevel: z.string().nullable(),
        accountSpendPerformance: z.string().nullable(),
        contactCreatedDate: z.string().nullable(),
        contactSpendStatus: z.string().nullable(),
        dbFirstBookingDate: z.string().nullable().optional(),
        dbLastBookingDate: z.string().nullable().optional(),
        email: z.string().nullable(),
        gender: z.string().nullable().optional(),
        id: z.string().nullable(),
        lastModified: z.string().nullable(),
        linkedIn: z.string().nullable().optional(),
        monthlySpend: z.array(CanonicalMonthlySpendSchema),
        name: z.string().nullable(),
        ninetyDayBookings: z.number().nullable(),
        ninetyDaySpend: z.number().nullable(),
        ninetyOneTo120DayBookings: z.number().nullable(),
        ninetyOneTo120DaySpend: z.number().nullable(),
        phone: z.string().nullable(),
        recordId: z.string().nullable(),
        relationshipManager: z.string().nullable(),
        role: z.string().nullable(),
        sixtyDayBookings: z.number().nullable(),
        sixtyDaySpend: z.number().nullable(),
        sixtyOneTo90DayBookings: z.number().nullable(),
        sixtyOneTo90DaySpend: z.number().nullable(),
        thirtyDayBookings: z.number().nullable(),
        thirtyDayCancellations: z.number().nullable(),
        thirtyDayQuotesConverted: z.number().nullable(),
        thirtyDayQuotesRequested: z.number().nullable(),
        thirtyDayReduction: z.number().nullable(),
        thirtyDaySpend: z.number().nullable(),
        thirtyOneTo60DayBookings: z.number().nullable(),
        thirtyOneTo60DaySpend: z.number().nullable(),
        trigger: z.boolean().nullable(),
    })
    .strict()

export type CanonicalCommercialContact = z.infer<
    typeof CanonicalCommercialContactSchema
>

// --- Activity (for account timeline) ---
export const CanonicalActivitySchema = z
    .object({
        date: z.string().nullable(),
        description: z.string().nullable(),
        id: z.string().nullable(),
        taskId: z.string().nullable().optional(),
        taskTitle: z.string().nullable().optional(),
        type: z.enum([
            'call',
            'email',
            'meeting',
            'note',
            'deal_created',
            'stage_change',
        ]),
        user: z.string().nullable(),
    })
    .strict()

export type CanonicalActivity = z.infer<typeof CanonicalActivitySchema>

// --- Commercial Account (canonical schema based on litter-deal-flow UI) ---
export const CanonicalCommercialAccountSchema = z
    .object({
        AOV: z.number().nullable(),
        GM: z.number().nullable(),
        accountActivityStatus: z.string().nullable(),
        accountLevel: AccountLevelSchema.nullable(),
        accountMovementStatus: z.string().nullable(),
        accountSpendLevel: SpendLevelSchema.nullable(),
        accountSpendPerformance: SpendPerformanceSchema.nullable(),
        accountSpendTargetDelta: z.string().nullable(),
        accountStatus: z.string().nullable(),
        accountsEmail: z.string().nullable(),
        // Phone number for the accounts team contact
        accountsTeamContactNumber: z.string().nullable(),
        activities: z.array(CanonicalActivitySchema).nullable(),
        aiEmailContent: z.string().nullable().optional(),
        aiKeywords: z.array(z.string()).nullable().optional(),
        aiOverview: z.string().nullable().optional(),
        aiPersonalisationContext: z.string().nullable().optional(),
        aiServicePotentialScore: z.number().nullable().optional(),
        aiServices: z.string().nullable().optional(),
        approvedDate: z.string().nullable(),
        bookingFormUrl: z.string().nullable(),
        businessEmail: z.string().nullable(),
        businessPhoneNumber: z.string().nullable(),
        classification: AccountClassificationSchema.nullable(),
        clientCategory: z.string().nullable(),
        clientGainedOrInherited: z.string().nullable(),
        clientStatus: z.string().nullable(),
        clientStatusTrend: z.string().nullable(),
        clientSubCategory: z.string().nullable(),
        clientTrend: z.string().nullable(),
        clientType: z.string().nullable(),
        commercialRecordId: z.string().nullable(),
        companyLegalName: z.string().nullable(),
        companyRegistrationNumber: z.string().nullable(),
        contacts: z.array(CanonicalCommercialContactSchema).nullable(),
        createdAt: z.string().nullable(),
        creditAvailable: z.number().nullable(),
        creditLimit: z.number().nullable(),
        creditLimitStatus: z.string().nullable(),
        creditsafeNetAssets: z.number().nullable(),
        creditsafeRiskDescription: z.string().nullable(),
        creditsafeScore: z.number().nullable(),
        creditsafeStatus: z.string().nullable(),
        creditsafeStatusDescription: z.string().nullable(),
        // Whether the job card will be shown on the client portal
        dashboardJobCard: z.boolean().nullable(),
        daysSinceLastBooking: z.number().nullable(),
        dbFirstBookingDate: z.string().nullable(),
        dbLastBookingDate: z.string().nullable(),
        desiredMargin: z.number().nullable(),
        // Auto-apply fee SKUs the account has opted out of (LITTA-1966).
        // .nullish() so an older litta-api build (without this field in its response) doesn't break the page.
        disabledAutoApplyFees: z.array(z.string()).nullish(),
        documentationEmail: z.string().nullable(),
        entityType: z.string().nullable(),
        estimatedClientValue: z.number().nullable(),
        estimatedNumEmployees: z.number().nullable().optional(),
        excludeFromAccountsTeamEmail: z.boolean().nullable(),
        expectedRevenuePerMonth: z.number().nullable(),
        externalAccountRef: z.string().nullable(),
        financialAccountType: FinancialAccountTypeSchema.nullable(),
        foundedYear: z.number().nullable().optional(),
        generalOnSiteInstructions: z.string().nullable().optional(),
        id: z.string().nullable(),
        imagePreferences: z.array(z.string()).nullable(),
        industry: z.string().nullable(),
        internalName: z.string().nullable(),
        internalRMNotes: z.string().nullable().optional(),
        invoicePo: z.boolean().nullable(),
        // This defines if a customer will get a Job Card or not
        jobCardOptOut: z.boolean().nullable(),
        lastBookingDate: z.string().nullable(),
        linkedinUrl: z.string().nullable().optional(),
        mainContact: z.string().nullable(),
        mainContactEmail: z.string().nullable(),
        monthlySpend: z.array(CanonicalMonthlySpendSchema).nullable(),
        ninetyDayAvgBookings: z.number().nullable(),
        ninetyDayBookings: z.number().nullable(),
        ninetyDaySpend: z.number().nullable(),
        onSiteMethodProcess: z.string().nullable().optional(),
        paymentTermDays: z.number().nullable(),
        paymentTermType: z.string().nullable(),
        portalImages: z.boolean().nullable(),
        ppeMandatory: z.boolean().nullable(),
        // PPE requirements list (e.g. HIGH VISIBILITY VEST, STEEL TOE CAP BOOTS). Not ready yet, can be ignored for now.
        ppeRequirements: z.array(z.string()).nullable(),
        prefJobCard: z.array(z.string()).nullable(),
        // Who gets booking reminders (missing photos / mandatory answers): 'siteContactSms' and/or 'siteContactEmail'.
        // .nullish() so an older litta-api build (without this field in its response) doesn't break the page.
        prefBookingReminder: z.array(z.string()).nullish(),
        prefTrackingLink: z.array(z.string()).nullable(),
        prefWtn: z.array(z.string()).nullable(),
        preferencesComplete: z.boolean().nullable(),
        // When true, all WTN / Tracking Link / Job Card sends are blocked for this account, overriding whatever the per-channel preference arrays say.
        // .nullish() so an older litta-api build (without this field in its response) doesn't break the page.
        suppressJobNotifications: z.boolean().nullish(),
        // Linked pricing document record IDs
        pricingDocumentLink: z.array(z.string()).nullable(),
        pricingTier: z.string().nullable(),
        // Linked pricing tier record IDs — always include Standard Additional Items alongside the main tier
        pricingTierLink: z.array(z.string()).nullable(),
        proposedAccountLevel: AccountLevelSchema.nullable(),
        // Clients that require PO for invoicing — invoice cannot be completed without a PO number
        purchaseOrderRequired: z.boolean().nullable(),
        quoteConversionRate: z.number().nullable(),
        registeredAddress: z.string().nullable(),
        relationshipManager: z.string().nullable(),
        relationshipManagerEmail: z.string().nullable(),
        relationshipManagerLink: z.string().nullable(),
        remainingWasteProcess: z.string().nullable().optional(),
        sicCode: z.string().nullable(),
        targetAccountStatus: z.string().nullable().optional(),
        sixtyDayBookings: z.number().nullable(),
        sixtyDaySpend: z.number().nullable(),
        thirtyDayBookings: z.number().nullable(),
        thirtyDaySpend: z.number().nullable(),
        thirtyOneTo60DayBookings: z.number().nullable(),
        thirtyOneTo60DaySpend: z.number().nullable(),
        // We request tipping tickets for any of these jobs
        tippingTicketMandatory: z.boolean().nullable(),
        totalOrders: z.number().nullable(),
        totalRevenue: z.number().nullable(),
        totalRevenue365: z.number().nullable(),
        vatNumber: z.string().nullable(),
        // Linked waste reporting module record IDs — drives wasteReportingLevel, wasteReportingFrequency, wasteReportingIncluded
        wasteReportModule: z.array(z.string()).nullable(),
        wasteReportingFrequency: z.string().nullable(),
        wasteReportingIncluded: z.array(z.string()).nullable(),
        wasteReportingLevel: WasteReportingLevelSchema.nullable(),
        wastedJourneyNextSteps: z.string().nullable().optional(),
        websiteUrl: z.string().nullable(),
        // When true, the client needs a weight value on the WTN — cannot be generated without it
        weightMandatory: z.boolean().nullable(),
        wtnImmediate: z.boolean().nullable(),
        wtnPo: z.boolean().nullable(),
        // This defines if the licence and the location of the disposal site is required — WTNs cannot be generated without it
        wtnTippingLocation: z.boolean().nullable(),
        // Clients that require WTNs to be sent with the invoice
        wtnWithInvoice: z.boolean().nullable(),
        xeroOutstandingBalance: z.number().nullable(),
        xeroOverdueBalance: z.number().nullable(),
    })
    .strict()

export type CanonicalCommercialAccount = z.infer<
    typeof CanonicalCommercialAccountSchema
>

// --- Paginated Response ---
export const PaginatedCommercialAccountsSchema = z.object({
    currentPage: z.number(),
    data: z.array(CanonicalCommercialAccountSchema),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    totalCount: z.number(),
    totalPages: z.number(),
})

export type PaginatedCommercialAccounts = z.infer<
    typeof PaginatedCommercialAccountsSchema
>
